/**
 * Unified dashboard - aggregates data across all user's organizations.
 *
 * Shows overview stats, organization quick links, and recent activity.
 */

import { organizationListQueryOptions } from "@/lib/queries/organization";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Activity,
  ArrowRight,
  Building2,
  FileText,
  Plus,
  TrendingUp,
  Users,
  UserStar,
} from "lucide-react";

export const Route = createFileRoute("/(app)/examples/")({
  component: Dashboard,
});

function Dashboard() {
  const { data: organizations, isLoading: orgsLoading } = useQuery(
    organizationListQueryOptions(),
  );

  // Organization list doesn't include members - this is placeholder data
  const totalMembers = organizations?.length ?? 0;

  const stats = [
    {
      title: "Accounts",
      value: organizations?.length?.toString() ?? "0",
      description: "Teams you belong to",
      icon: Building2,
    },
    {
      title: "Total Members",
      value: totalMembers.toString(),
      description: "Across all accounts",
      icon: Users,
    },
    {
      title: "Active Sessions",
      value: "—",
      description: "Coming soon",
      icon: Activity,
    },
    {
      title: "Growth Rate",
      value: "—",
      description: "Coming soon",
      icon: TrendingUp,
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Dashboard</h2>
        <p className="text-muted-foreground">
          Welcome back! Here's an overview across all your accounts.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">
                {stat.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Organizations Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Your Accounts</CardTitle>
              <CardDescription>
                Quick access to your teams and workspaces
              </CardDescription>
            </div>
            <Link to="/">
              <Button variant="outline" size="sm">
                View All
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {orgsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-24 rounded-lg bg-muted animate-pulse"
                />
              ))}
            </div>
          ) : organizations?.length === 0 ? (
            <div className="text-center py-8">
              <UserStar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                No creator accounts yet
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create or add a creator account to get started
              </p>
              <Link to="/">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Creator Account
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {organizations?.slice(0, 6).map((org) => (
                <Link
                  key={org.id}
                  to="/o/$orgSlug"
                  params={{ orgSlug: org.slug }}
                  className="block"
                >
                  <div className="p-4 border rounded-lg hover:bg-accent transition-colors">
                    <div className="flex items-center gap-3">
                      {org.logo ? (
                        <img
                          src={org.logo}
                          alt={org.name}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <UserStar className="h-5 w-5 text-primary" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{org.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {org.role ?? "member"}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </Link>
              ))}
              {(organizations?.length ?? 0) > 6 && (
                <Link to="/" className="block">
                  <div className="p-4 border border-dashed rounded-lg hover:bg-accent transition-colors h-full flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-sm font-medium">
                        +{(organizations?.length ?? 0) - 6} more
                      </p>
                      <p className="text-xs text-muted-foreground">View all</p>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>
              Latest events across your accounts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {organizations && organizations.length > 0 ? (
                organizations.slice(0, 4).map((org) => (
                  <div key={org.id} className="flex items-center gap-4">
                    <div className="h-2 w-2 rounded-full bg-primary" />
                    <div className="flex-1">
                      <p className="text-sm">
                        Joined <span className="font-medium">{org.name}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(org.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-xs capitalize">
                      {org.role ?? "member"}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No recent activity
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks and operations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <Link
                to="/"
                className="p-4 text-left border rounded-lg hover:bg-accent transition-colors"
              >
                <UserStar className="h-5 w-5 mb-2" />
                <p className="text-sm font-medium">Manage Accounts</p>
              </Link>
              <button
                type="button"
                className="p-4 text-left border rounded-lg hover:bg-accent transition-colors"
              >
                <Users className="h-5 w-5 mb-2" />
                <p className="text-sm font-medium">Invite Members</p>
              </button>
              <button
                type="button"
                className="p-4 text-left border rounded-lg hover:bg-accent transition-colors"
              >
                <FileText className="h-5 w-5 mb-2" />
                <p className="text-sm font-medium">Generate Report</p>
              </button>
              <button
                type="button"
                className="p-4 text-left border rounded-lg hover:bg-accent transition-colors"
              >
                <Activity className="h-5 w-5 mb-2" />
                <p className="text-sm font-medium">View Analytics</p>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
